import { joiResolver } from '@hookform/resolvers/joi';
import { useRouter } from 'next/router';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { AppleLogo } from '@src/components/atoms/icons/apple-logo';
import { Check } from '@src/components/atoms/icons/check';
import { GoogleLogo } from '@src/components/atoms/icons/google';
import { Button } from '@src/components/atoms/inputs/button';
import Seo from '@src/components/common/seo/seo';
import { InputController } from '@src/components/input-controllers/input-controller';
import { Form } from '@src/components/molecules/form';
import { schemaMessages } from '@src/constants/schema-messages';
import { useBrand } from '@src/contexts/brand-context';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { AuthLayout } from '@src/features/auth/components/auth-layout';
import { SocialProvider } from '@src/features/auth/services/i-auth-service';
import { useErrorMessages } from '@src/hooks/use-error-messages';
import { useLocale } from '@src/hooks/use-locale';

import { overviewSchema } from './schema';
import styles from './styles.module.css';
import { Overview, OverviewFormProps } from './types';

type SocialProviderInfo = {
  provider: SocialProvider;
  name: string;
  icon: React.ReactNode;
};

const socialProviders: SocialProviderInfo[] = [
  {
    provider: 'google',
    name: 'Google',
    icon: <GoogleLogo />,
  },
  {
    provider: 'apple',
    name: 'Apple',
    icon: <AppleLogo />,
  },
];

const OverviewForm = ({
  onSubmit,
  onSocialLogin,
  isLoading,
  socialProvider,
  email: defaultEmail,
}: OverviewFormProps) => {
  const content = useCmsContent();
  const locale = useLocale();
  const router = useRouter();
  const { brand } = useBrand();
  const websiteUrl = new URL(`/${locale}`, brand.websiteUrl);
  const pagesWebsiteUrl = new URL(`/${locale}`, 'https://pages.numastays.com');

  const source = router.query.source as string;

  const constructUrl = (base: URL, path: string) => {
    return new URL(path, base).toString();
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<Overview>({
    defaultValues: {
      email: defaultEmail,
    },
    resolver: joiResolver(overviewSchema, {
      messages: schemaMessages,
    }),
    criteriaMode: 'all',
  });

  const { email } = watch();
  const errorMessages = useErrorMessages<Overview>(errors);

  return (
    <Form onSubmit={handleSubmit(() => onSubmit(email))}>
      <Seo title={content.overviewFormHeading} />

      <AuthLayout.Header source={source}>
        <div className={styles.TitleHeader}>{content.overviewFormHeadingTitle}</div>
        {content?.overviewFormHeadingMemberBenefits?.length > 0 && (
          <div className={styles.SubTitleHeader}>
            {content.overviewFormHeadingMemberBenefits.map(({ id, name }) => (
              <div className={styles.ItemSubTitleHeader} key={id}>
                <Check />
                {name}
              </div>
            ))}
          </div>
        )}
      </AuthLayout.Header>

      <div className={styles.Wrapper}>
        <InputController
          name="email"
          type="email"
          autoComplete="email"
          error={errorMessages.email}
          label={content.labelEmail}
          control={control}
        />
        <Button size="small" type="submit" isLoading={isLoading && !socialProvider}>
          {content.continueButton}
        </Button>
      </div>

      <Form.Divider text="or" />

      <div className={styles.Wrapper}>
        {socialProviders.map(({ name, provider, icon }) => (
          <Button
            key={provider}
            size="small"
            variant="secondary"
            type="button"
            onClick={() => onSocialLogin(provider)}
            isLoading={isLoading && socialProvider === provider}
          >
            <div className={styles.ButtonContent}>
              {icon}
              <FormattedMessage id="continueWith" defaultMessage={content.continueWith} values={{ provider: name }} />
            </div>
          </Button>
        ))}
      </div>
      <div className={styles.Disclaimer}>
        {content?.headingDisclaimerOverviewFrom.split('\n').map((line, index) => (
          <div key={`${line}-${index}`}>
            <FormattedMessage
              id="overviewFormDisclaimer"
              defaultMessage={line}
              values={{
                'link-to-benefits': (chunks) => (
                  <a
                    href={constructUrl(pagesWebsiteUrl, content.becomeMemberRoute.path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunks}
                  </a>
                ),
                'link-to-terms': (chunks) => (
                  <a href={constructUrl(websiteUrl, content.termsRoute.path)} target="_blank" rel="noreferrer">
                    {chunks}
                  </a>
                ),
                'link-to-privacy': (chunks) => (
                  <a href={constructUrl(websiteUrl, content.privacyRoute.path)} target="_blank" rel="noreferrer">
                    {chunks}
                  </a>
                ),
              }}
            />
          </div>
        ))}
      </div>
    </Form>
  );
};
export default OverviewForm;
